<template>
  <div class="geometry-edit-toolbar">
    <div class="toolbar-section tools">
      <!-- Always visible: Select tool -->
      <button class="toolbar-btn" :class="{ active: activeTool === 'select' }" @click="selectTool('select')"
        title="Select sector" data-cy="toolbar-select">
        <i class="fa fa-hand-o-up"></i>
        <span class="btn-label">Select</span>
      </button>

      <div class="toolbar-divider"></div>

      <!-- Always visible: New Sector (Draw Polygon) -->
      <button class="toolbar-btn" :class="{ active: activeTool === 'new-sector' }" @click="selectTool('new-sector')"
        title="Create new sector" data-cy="toolbar-new-sector">
        <i class="fa fa-plus-circle"></i>
        <span class="btn-label">New Sector</span>
      </button>

      <div class="toolbar-divider"></div>

      <!-- Visible only when sector selected: Edit Shape -->
      <button v-show="hasSelection" class="toolbar-btn" :class="{ active: activeTool === 'edit-shape' }"
        @click="selectTool('edit-shape')" title="Edit vertices" data-cy="toolbar-edit-shape">
        <i class="fa fa-edit"></i>
        <span class="btn-label">Edit Shape</span>
      </button>

      <!-- Visible only when sector selected: Move -->
      <button v-show="hasSelection" class="toolbar-btn" :class="{ active: activeTool === 'move' }"
        @click="selectTool('move')" title="Move/drag polygon" data-cy="toolbar-move">
        <i class="fa fa-arrows"></i>
        <span class="btn-label">Move</span>
      </button>

      <!-- Visible only when sector selected: Delete -->
      <button v-show="hasSelection" class="toolbar-btn" :class="{ active: activeTool === 'delete' }"
        @click="selectTool('delete')" :title="deleteTooltip" data-cy="toolbar-delete">
        <i class="fa fa-trash"></i>
        <span class="btn-label">{{ deleteLabel }}</span>
      </button>

      <!-- Visible only when sector selected: Deselect -->
      <button v-show="hasSelection" class="toolbar-btn"
        @click="deselect" title="Deselect sector" data-cy="toolbar-deselect">
        <i class="fa fa-times-circle"></i>
        <span class="btn-label">Deselect</span>
      </button>
    </div>

    <div class="toolbar-section history">
      <button class="toolbar-btn" @click="undo" :disabled="!canUndo" title="Undo (Ctrl+Z)" data-cy="toolbar-undo">
        <i class="fa fa-undo"></i>
        <span class="btn-label">Undo</span>
      </button>

      <button class="toolbar-btn" @click="redo" :disabled="!canRedo" title="Redo (Ctrl+Y)" data-cy="toolbar-redo">
        <i class="fa fa-redo"></i>
        <span class="btn-label">Redo</span>
      </button>
    </div>

    <div class="toolbar-divider"></div>

    <div class="toolbar-section actions">
      <button class="toolbar-btn btn-save" @click="save" :disabled="!canSave || !isDirty" title="Save changes" data-cy="toolbar-save">
        <i class="fa fa-save"></i>
        <span class="btn-label">Save</span>
      </button>

      <button class="toolbar-btn btn-cancel" @click.stop.prevent="cancel" :disabled="!isDirty" title="Cancel editing" data-cy="toolbar-cancel">
        <i class="fa fa-times"></i>
        <span class="btn-label">Cancel</span>
      </button>
    </div>

    <!-- Mobile-friendly toolbar -->
    <div class="mobile-toolbar" v-if="isMobile">
      <div class="mobile-tools">
        <button v-for="tool in mobileTools" :key="tool.id" v-show="!tool.hidden" class="mobile-tool-btn"
          :class="{ active: activeTool === tool.id }" @click="tool.id === 'deselect' ? deselect() : selectTool(tool.id)" :disabled="tool.disabled">
          <i :class="tool.icon"></i>
        </button>
      </div>

      <div class="mobile-actions">
        <button class="mobile-action-btn" @click="toggleMoreOptions">
          <i class="fa fa-ellipsis-v"></i>
        </button>
      </div>
    </div>

    <!-- Mobile options menu -->
    <transition name="slide">
      <div v-if="showMobileOptions" class="mobile-options">
        <button @click="undo" :disabled="!canUndo">
          <i class="fa fa-undo"></i> Undo
        </button>
        <button @click="redo" :disabled="!canRedo">
          <i class="fa fa-redo"></i> Redo
        </button>
        <hr>
        <button @click="save" :disabled="!canSave || !isDirty" class="save">
          <i class="fa fa-save"></i> Save Changes
        </button>
        <button @click="cancel" :disabled="!isDirty" class="cancel">
          <i class="fa fa-times"></i> Cancel
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'GeometryEditToolbar',
  props: {
    activeTool: {
      type: String,
      default: 'select'
    },
    canUndo: {
      type: Boolean,
      default: false
    },
    canRedo: {
      type: Boolean,
      default: false
    },
    canSave: {
      type: Boolean,
      default: true
    },
    isDirty: {
      type: Boolean,
      default: false
    },
    // 'sector' (default) or 'part' — flips Delete button copy when a MultiPolygon part is sub-selected
    deleteTarget: {
      type: String,
      default: 'sector',
      validator: (v) => v === 'sector' || v === 'part'
    }
  },
  data() {
    return {
      isMobile: false,
      showMobileOptions: false
    };
  },
  computed: {
    ...mapState('serviceAreas', ['selectedFeature']),
    hasSelection() {
      return !!this.selectedFeature;
    },
    deleteLabel() {
      return this.deleteTarget === 'part' ? 'Delete Part' : 'Delete';
    },
    deleteTooltip() {
      return this.deleteTarget === 'part' ? 'Delete this part' : 'Delete sector';
    },
    mobileTools() {
      return [
        { id: 'select', icon: 'fa fa-mouse-pointer', disabled: false },
        { id: 'new-sector', icon: 'fa fa-plus-circle', disabled: false },
        { id: 'edit-shape', icon: 'fa fa-edit', disabled: !this.hasSelection, hidden: !this.hasSelection },
        { id: 'move', icon: 'fa fa-arrows', disabled: !this.hasSelection, hidden: !this.hasSelection },
        { id: 'delete', icon: 'fa fa-trash', disabled: !this.hasSelection, hidden: !this.hasSelection },
        { id: 'deselect', icon: 'fa fa-times-circle', disabled: !this.hasSelection, hidden: !this.hasSelection }
      ];
    }
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);

    // Keyboard shortcuts
    document.addEventListener('keydown', this.handleKeyboard);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
    document.removeEventListener('keydown', this.handleKeyboard);
  },
  methods: {
    selectTool(tool) {
      this.$emit('tool-change', tool);
      this.showMobileOptions = false;
    },
    undo() {
      this.$emit('undo');
    },
    redo() {
      this.$emit('redo');
    },
    save() {
      this.$emit('save');
      this.showMobileOptions = false;
    },
    cancel() {
      this.$emit('cancel');
      this.showMobileOptions = false;
    },
    deselect() {
      this.$emit('deselect');
    },
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },
    toggleMoreOptions() {
      this.showMobileOptions = !this.showMobileOptions;
    },
    handleKeyboard(e) {
      // Ctrl/Cmd + Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (this.canUndo) this.undo();
      }

      // Ctrl/Cmd + Y or Ctrl/Cmd + Shift + Z for redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        if (this.canRedo) this.redo();
      }

      // Escape to cancel current tool - return to select
      if (e.key === 'Escape') {
        this.selectTool('select');
      }

      // Delete key to delete selection
      if (e.key === 'Delete' && this.hasSelection) {
        this.selectTool('delete');
      }
    }
  }
};
</script>

<style scoped>
.geometry-edit-toolbar {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  display: flex;
  padding: 4px;
  gap: 4px;
  z-index: 1000;
}

/* Fullscreen mode - ensure toolbar stays visible */
.geometry-edit-toolbar.toolbar-fullscreen {
  position: fixed !important;
  z-index: 10000 !important;
}

.toolbar-section {
  display: flex;
  gap: 4px;
}

.toolbar-divider {
  width: 1px;
  background: #ddd;
  margin: 0 4px;
}

.toolbar-btn {
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  min-width: 60px;
  justify-content: center;
}

.toolbar-btn:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #999;
}

.toolbar-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toolbar-btn.active {
  background: #2c5aa8;
  color: white;
  border-color: #2c5aa8;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
  transform: translateY(1px);
}

.toolbar-btn i {
  font-size: 16px;
}

.btn-label {
  font-size: 12px;
  font-weight: 500;
}

.btn-save {
  background: #28a745;
  color: white;
  border-color: #28a745;
}

.btn-save:hover:not(:disabled) {
  background: #218838;
  border-color: #1e7e34;
}

.btn-cancel {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
}

.btn-cancel:hover {
  background: #c82333;
  border-color: #bd2130;
}

/* Mobile styles */
.mobile-toolbar {
  display: none;
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 25px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  padding: 8px 16px;
  gap: 8px;
  z-index: 1000;
}

.mobile-tools {
  display: flex;
  gap: 8px;
}

.mobile-tool-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: 2px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.mobile-tool-btn.active {
  background: #2c5aa8;
  color: white;
  border-color: #2c5aa8;
}

.mobile-tool-btn:disabled {
  opacity: 0.5;
}

.mobile-action-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #666;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.mobile-options {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  padding: 8px 0;
  min-width: 200px;
  z-index: 999;
}

.mobile-options button {
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
}

.mobile-options button:hover {
  background: #f5f5f5;
}

.mobile-options button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mobile-options button.save {
  color: #28a745;
}

.mobile-options button.cancel {
  color: #dc3545;
}

.mobile-options hr {
  margin: 4px 0;
  border: none;
  border-top: 1px solid #ddd;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s, opacity 0.3s;
}

.slide-enter,
.slide-leave-to {
  transform: translateX(-50%) translateY(20px);
  opacity: 0;
}

@media (max-width: 767px) {
  .geometry-edit-toolbar {
    display: none;
  }

  .mobile-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}

/* Icon fixes for Font Awesome */
.fa-draw-polygon:before {
  content: "\f5ee";
  /* Using shapes icon as fallback */
}

.fa-redo:before {
  content: "\f01e";
  /* Using rotate-right icon */
}
</style>
